import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, MessageSquare } from 'lucide-react';
import { Button } from '../ui/Button.jsx';
import { EmptyState } from '../ui/EmptyState.jsx';
import { Skeleton } from '../ui/Skeleton.jsx';
import { Alert } from '../ui/Alert.jsx';
import { useSession } from '../../context/SessionContext';
import { useAuth } from '../../context/AuthContext';
import { scrollBehavior } from '../../utils/motion';
import {
  deriveGreetingFirstName,
  resolveGreeting,
  resolveKnowledgeAudience,
  resolveKnowledgeTitle,
} from '../../utils/chatWelcome.js';
import Message from './Message.jsx';

/**
 * The chat thread (design spec §10.2).
 *
 * Owns the empty/loading/streaming/error presentation of the conversation
 * itself; the live-region announcement lives one level up in ChatView so it is
 * not remounted with the list.
 */
export function ChatThread({
  messages = [],
  isStreaming = false,
  streamingText = '',
  streamingSources = [],
  loading = false,
  error = null,
  onSuggestionClick,
  fileContext = null,
  activeSourceId = null,
  onOpenSource,
  onRate,
  onRetry,
}) {
  const bottomRef = useRef(null);
  const { t } = useTranslation();
  const { sessionMode, codebaseContext, selectedPersona } = useSession();
  const { user } = useAuth();
  const [axiomSuggestions, setAxiomSuggestions] = useState(null);
  const [axiomConnected, setAxiomConnected] = useState(false);

  const audience = resolveKnowledgeAudience(user?.role);
  const knowledgeSuggestions = (aud) => (aud === 'admin'
    ? [t('chat.knowledgeSuggestionAdmin1'), t('chat.knowledgeSuggestionAdmin2'), t('chat.knowledgeSuggestionAdmin3')]
    : [t('chat.knowledgeSuggestionLearner1'), t('chat.knowledgeSuggestionLearner2'), t('chat.knowledgeSuggestionLearner3')]);

  // Probe Axiom so the empty state proposes questions about the corpus the
  // user actually has, not generic prompts.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/axiom/documents')
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data?.documents?.length) return;
        setAxiomConnected(true);

        const docs = data.documents.filter(d => d.status === 'ready');
        const filenames = docs.map(d => d.filename).filter(Boolean);
        const laws = filenames.filter(n => /§|BetrVG|DSGVO|BDSG|ISO|GmbHG|AktG|HGB/i.test(n));
        const helpDocs = filenames.filter(n => /Hilfe-Center|Help Center|Handbuch|Manual|Anleitung|Guide/i.test(n));

        const suggestions = [];
        if (helpDocs.length > 0) {
          const sample = helpDocs[Math.floor(Math.random() * helpDocs.length)];
          const action = sample.split('|')[0].split(' in ')[0].trim();
          if (action) suggestions.push(t('chat.knowledgeHowToSuggestion', { topic: action }));
        }
        if (laws.length > 0) {
          const sample = laws[Math.floor(Math.random() * laws.length)];
          suggestions.push(t('chat.knowledgeRuleSuggestion', { doc: sample.replace(/ - Einzelnorm$/, '') }));
        }
        // Pad with the role-appropriate defaults rather than more
        // corpus-derived guesses — this is already the best-effort path.
        for (const fallback of knowledgeSuggestions(resolveKnowledgeAudience(user?.role))) {
          if (suggestions.length >= 3) break;
          suggestions.push(fallback);
        }
        setAxiomSuggestions(suggestions.slice(0, 3));
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, user?.role]);

  const effectiveMode = sessionMode === 'axiom' || (axiomConnected && !codebaseContext)
    ? 'axiom'
    : sessionMode;

  useEffect(() => {
    // An explicit `behavior` overrides the CSS `scroll-behavior` reset, so the
    // reduced-motion preference has to be read here rather than relied on.
    bottomRef.current?.scrollIntoView({ behavior: scrollBehavior(), block: 'end' });
  }, [messages.length, streamingText]);

  const emptyContent = useMemo(() => {
    const fileName = fileContext?.split('/').pop() || '';
    const greeting = resolveGreeting(deriveGreetingFirstName({
      firstName: user?.first_name,
      email: user?.email,
      username: user?.username,
    }));
    const title = t(greeting.key, greeting.params);

    if (effectiveMode === 'axiom') {
      const knowledgeTitle = resolveKnowledgeTitle(user?.tenant_name);
      return {
        icon: <BookOpen size={28} />,
        title: t(knowledgeTitle.key, knowledgeTitle.params),
        body: [title, t(`chat.knowledgeSubtitle${audience === 'admin' ? 'Admin' : 'Learner'}`)],
        suggestions: axiomSuggestions || knowledgeSuggestions(audience),
      };
    }
    if (fileContext) {
      return {
        icon: <MessageSquare size={28} />,
        title,
        body: t('chat.fileSuggestionSubtitle', { name: fileName }),
        suggestions: [
          t('chat.fileSuggestion1', { path: fileContext }),
          t('chat.fileSuggestion2', { path: fileContext }),
          t('chat.fileSuggestion3', { path: fileContext }),
        ],
      };
    }
    return {
      icon: <MessageSquare size={28} />,
      title,
      body: t('chat.emptyBody'),
      suggestions: [t('chat.suggestion1'), t('chat.suggestion2'), t('chat.suggestion3')],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMode, fileContext, axiomSuggestions, audience, user, t]);

  if (loading) {
    return (
      <div className="etch-chat__thread" data-state="loading">
        <div className="etch-chat__column etch-stack">
          <p className="etch-visually-hidden" role="status">{t('states.loading')}</p>
          <Skeleton height="3rem" />
          <Skeleton height="6rem" />
          <Skeleton height="4rem" />
        </div>
      </div>
    );
  }

  if (messages.length === 0 && !isStreaming) {
    return (
      <div className="etch-chat__thread" data-state="empty">
        <div className="etch-chat__column">
          {error && <Alert tone="danger" title={t('states.error')}>{error}</Alert>}
          <EmptyState icon={emptyContent.icon} title={emptyContent.title}>
            {(Array.isArray(emptyContent.body) ? emptyContent.body : [emptyContent.body]).map((line, index) => (
              <p key={index}>{line}</p>
            ))}
            <ul className="etch-chat__suggestions">
              {emptyContent.suggestions.map((suggestion, index) => (
                <li key={index}>
                  <Button variant="secondary" size="compact" onClick={() => onSuggestionClick?.(suggestion)}>
                    {suggestion}
                  </Button>
                </li>
              ))}
            </ul>
          </EmptyState>
        </div>
      </div>
    );
  }

  return (
    <div className="etch-chat__thread" data-state={isStreaming ? 'streaming' : 'ready'}>
      <div className="etch-chat__column">
        {messages.map((message, index) => (
          <Message
            key={message.id}
            message={message}
            isLast={!isStreaming && index === messages.length - 1 && message.role === 'assistant'}
            activeSourceId={activeSourceId}
            onOpenSource={onOpenSource}
            onSuggestionClick={onSuggestionClick}
            onRate={onRate}
            onRetry={onRetry}
          />
        ))}
        {isStreaming && (
          <Message
            key="streaming"
            streaming
            message={{
              id: 'streaming',
              role: 'assistant',
              content: streamingText,
              timestamp: null,
              // The in-flight answer is the one turn where the live persona IS
              // the author, so it is the only place that may read it.
              persona: selectedPersona?.name
                ? { id: selectedPersona.personaId ?? null, name: selectedPersona.name }
                : null,
            }}
            sources={streamingSources}
            activeSourceId={activeSourceId}
            onOpenSource={onOpenSource}
          />
        )}
        {error && !isStreaming && (
          <Alert tone="danger" title={t('states.error')}>
            {error}
            {onRetry && (
              <Button variant="secondary" size="compact" onClick={onRetry}>
                {t('states.retry')}
              </Button>
            )}
          </Alert>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

export default ChatThread;
